import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Package, Utensils } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function MaterialDialog({ material, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(material?.name || "");
  const [unit, setUnit] = useState(material?.unit || "pcs");
  const [price, setPrice] = useState(material?.price_per_unit || 0);

  const save = async () => {
    if (!name.trim()) return toast.error("Nama bahan wajib diisi.");
    const payload = { name: name.trim(), unit: unit.trim() || "pcs", price_per_unit: Number(price) || 0 };
    try {
      if (material) await api.put(`/materials/${material.id}`, payload);
      else await api.post("/materials", payload);
      toast.success("Bahan tersimpan.");
      setOpen(false);
      if (!material) { setName(""); setUnit("pcs"); setPrice(0); }
      onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {material ? (
          <button className="text-muted-foreground hover:text-primary" data-testid={`edit-material-${material.id}`}><Pencil className="h-4 w-4" /></button>
        ) : (
          <Button className="rounded-xl min-h-[44px]" data-testid="add-material-button"><Plus className="mr-2 h-4 w-4" /> Tambah Bahan</Button>
        )}
      </DialogTrigger>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle>{material ? "Edit Bahan" : "Tambah Bahan Baku"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Nama Bahan</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="cth: Kopi Robusta" className="rounded-xl" data-testid="material-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Satuan</Label>
              <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="gram / ml / pcs" className="rounded-xl" data-testid="material-unit-input" /></div>
            <div className="space-y-1.5"><Label>Harga / Satuan (Rp)</Label>
              <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="rounded-xl font-mono" data-testid="material-price-input" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} className="rounded-xl" data-testid="material-save-button">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Resep() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: materials = [] } = useQuery({ queryKey: ["materials"], queryFn: async () => (await api.get("/materials")).data });
  const { data: recipes = [] } = useQuery({ queryKey: ["recipes"], queryFn: async () => (await api.get("/recipes")).data });

  const refetchMat = () => qc.invalidateQueries({ queryKey: ["materials"] });
  const refetchRcp = () => qc.invalidateQueries({ queryKey: ["recipes"] });

  const delMaterial = async (id) => { await api.delete(`/materials/${id}`); toast.success("Bahan dihapus."); refetchMat(); };
  const delRecipe = async (id) => { await api.delete(`/recipes/${id}`); toast.success("Resep dihapus."); refetchRcp(); };
  const dupRecipe = async (id) => { await api.post(`/recipes/${id}/duplicate`); toast.success("Resep diduplikat."); refetchRcp(); };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Bahan Baku & Resep</h1>
        <p className="text-sm text-muted-foreground">Kelola daftar bahan dan resep produk Anda.</p>
      </div>

      <Tabs defaultValue="resep">
        <TabsList className="rounded-xl">
          <TabsTrigger value="resep" className="rounded-lg" data-testid="tab-resep"><Utensils className="mr-1.5 h-4 w-4" /> Resep & Produk</TabsTrigger>
          <TabsTrigger value="bahan" className="rounded-lg" data-testid="tab-bahan"><Package className="mr-1.5 h-4 w-4" /> Bahan Baku</TabsTrigger>
        </TabsList>

        <TabsContent value="resep" className="mt-5">
          <div className="mb-4 flex justify-end">
            <Button className="rounded-xl min-h-[44px]" onClick={() => navigate("/kalkulator")} data-testid="add-recipe-button">
              <Plus className="mr-2 h-4 w-4" /> Buat Resep Baru
            </Button>
          </div>
          {recipes.length === 0 ? (
            <Card className="rounded-2xl p-10 text-center text-muted-foreground">Belum ada resep. Buat resep pertama Anda.</Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {recipes.map((r) => (
                <Card key={r.id} className="flex flex-col rounded-2xl p-5" data-testid={`recipe-card-${r.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge variant="secondary" className="rounded-md text-xs">{r.category}</Badge>
                      <h3 className="mt-2 font-heading text-lg font-semibold leading-tight">{r.name}</h3>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div><p className="text-xs text-muted-foreground">HPP/unit</p><p className="font-mono font-semibold">{rupiah(r.computed.hpp_per_unit)}</p></div>
                    <div><p className="text-xs text-muted-foreground">Harga Jual</p><p className="font-mono font-semibold text-primary">{rupiah(r.computed.selling_price)}</p></div>
                    <div><p className="text-xs text-muted-foreground">Untung/unit</p><p className="font-mono font-semibold">{rupiah(r.computed.profit_per_unit)}</p></div>
                    <div><p className="text-xs text-muted-foreground">Markup</p><p className="font-mono font-semibold">{r.margin_percent}%</p></div>
                  </div>
                  <div className="mt-4 flex gap-2 border-t border-border pt-4">
                    <Button size="sm" variant="secondary" className="flex-1 rounded-lg" data-testid={`edit-recipe-${r.id}`}
                      onClick={() => navigate("/kalkulator", { state: { recipe: r } })}>
                      <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-lg" onClick={() => dupRecipe(r.id)} data-testid={`dup-recipe-${r.id}`}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="rounded-lg text-destructive" data-testid={`del-recipe-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-2xl">
                        <AlertDialogHeader><AlertDialogTitle>Hapus resep?</AlertDialogTitle>
                          <AlertDialogDescription>Resep "{r.name}" akan dihapus permanen.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
                          <AlertDialogAction className="rounded-xl bg-destructive hover:bg-destructive/90" onClick={() => delRecipe(r.id)}>Hapus</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="bahan" className="mt-5">
          <div className="mb-4 flex justify-end"><MaterialDialog onSaved={refetchMat} /></div>
          <Card className="overflow-hidden rounded-2xl">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60">
                  <TableHead>Nama Bahan</TableHead>
                  <TableHead>Satuan</TableHead>
                  <TableHead className="text-right">Harga/Satuan</TableHead>
                  <TableHead className="w-24 text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {materials.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Belum ada bahan baku.</TableCell></TableRow>
                ) : materials.map((m) => (
                  <TableRow key={m.id} data-testid={`material-row-${m.id}`}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell>{m.unit}</TableCell>
                    <TableCell className="text-right font-mono">{rupiah(m.price_per_unit)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-3">
                        <MaterialDialog material={m} onSaved={refetchMat} />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="text-muted-foreground hover:text-destructive" data-testid={`del-material-${m.id}`}><Trash2 className="h-4 w-4" /></button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-2xl">
                            <AlertDialogHeader><AlertDialogTitle>Hapus bahan?</AlertDialogTitle>
                              <AlertDialogDescription>"{m.name}" akan dihapus.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
                              <AlertDialogAction className="rounded-xl bg-destructive hover:bg-destructive/90" onClick={() => delMaterial(m.id)}>Hapus</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
